import { useState, type FormEvent } from "react";
import { useLogin } from "ra-core";

import { Notification } from "@/components/admin/notification";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";

import { fetchUsersWithKey } from "./authProvider";
import { isFakeZinspector, type ZiUser } from "./client";

const userLabel = (user: ZiUser): string =>
  `${user.FirstName ?? ""} ${user.LastName ?? ""}`.trim() ||
  user.email ||
  `User ${user.id}`;

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : "Sign-in failed.";

/**
 * Two steps: paste the zInspector API key (checked against the API), then pick yourself
 * from the team list so new deals get the right owner.
 */
export const ZinspectorLoginPage = () => {
  const { darkModeLogo, title } = useConfigurationContext();
  const login = useLogin();
  const [apiKey, setApiKey] = useState("");
  const [remember, setRemember] = useState(false);
  const [users, setUsers] = useState<ZiUser[] | null>(null);
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const checkKey = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const team = await fetchUsersWithKey(apiKey);
      if (!team.length)
        throw new Error("This key can't see any zInspector user.");
      setUsers(team);
      setUserId(team.length === 1 ? String(team[0].id) : "");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ apiKey, userId: Number(userId), remember });
    } catch (e) {
      setError(errorText(e));
      setLoading(false);
    }
  };

  const changeKey = () => {
    setUsers(null);
    setUserId("");
    setError(null);
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
          <div className="absolute inset-0 bg-zinc-900" />
          <div className="relative z-20 flex items-center text-lg font-medium">
            <img className="h-6 mr-2" src={darkModeLogo} alt={title} />
            {title}
          </div>
        </div>
        <div className="flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
              <p className="text-sm text-muted-foreground">
                {users
                  ? "Who are you in zInspector?"
                  : isFakeZinspector()
                    ? "Demo: any key works. The data is sample data and resets on reload."
                    : "Paste your zInspector API key."}
              </p>
            </div>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            {users ? (
              <form className="space-y-6" onSubmit={signIn}>
                <div className="space-y-2">
                  <Label htmlFor="zi-user">You are</Label>
                  <Select value={userId} onValueChange={setUserId}>
                    <SelectTrigger id="zi-user" className="w-full">
                      <SelectValue placeholder="Pick yourself" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={String(user.id)}>
                          {userLabel(user)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Button type="submit" disabled={loading || !userId}>
                    Sign in
                  </Button>
                  <Button type="button" variant="ghost" onClick={changeKey}>
                    Use another key
                  </Button>
                </div>
              </form>
            ) : (
              <form className="space-y-6" onSubmit={checkKey}>
                <div className="space-y-2">
                  <Label htmlFor="zi-api-key">API key</Label>
                  <Input
                    id="zi-api-key"
                    type="password"
                    autoComplete="off"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="zi-remember"
                    checked={remember}
                    onCheckedChange={(checked) => setRemember(checked === true)}
                  />
                  <Label htmlFor="zi-remember" className="font-normal">
                    Remember me on this device
                  </Label>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !apiKey.trim()}
                >
                  Continue
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
